import { c as _c } from "react/compiler-runtime";
/**
 * Patient-related data fetchers
 */
import { QueryClient, MutationOptions, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { updatePatientStatus, createOrUpdatePatient } from "@/src/actions/patient";
import { toast } from "@/src/hooks/use-toast";
import { REFERRALS_CACHE_KEY } from "@/src/lib/data-fetchers/referrals-fetchers";
import { TASKS_CACHE_KEY, TASK_CACHE_KEY } from "@/src/lib/data-fetchers/tasks-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { Patient, PatientStatus, PatientType } from "@/src/models/patient";
import { PatientMetadata } from "@/src/models/patient-metadata";
import { getPatientById, getPatientsByIds, getPatientsMetadataByIds, getPaginatedPatientsMetadata, searchPatientsByQuery, type PaginatedPatientsMetadataResponse } from "@/src/requests/patient";
import { isNotFound } from "@/src/requests/utils/fetch-json";
export const PATIENTS_CACHE_KEY = "patients";
export const LEADS_CACHE_KEY = "leads";
export const PAGINATED_PATIENTS_METADATA_CACHE_KEY = "paginated-patients-metadata";
export const PATIENTS_METADATA_BY_IDS_CACHE_KEY = "patients-metadata-by-ids";
export const PATIENTS_SEARCH_CACHE_KEY = "patients-search";
const deDuplicatePatients = (patients: Patient[]) => {
  // First sort patients to prioritize those with more complete information
  const sortedPatients = [...patients].sort((a, b) => {
    // Prioritize patients with more complete information
    const aComplete = !!(a.givenName && a.familyName && a.dateOfBirth);
    const bComplete = !!(b.givenName && b.familyName && b.dateOfBirth);
    if (aComplete && !bComplete) return -1;
    if (!aComplete && bComplete) return 1;
    return 0;
  });

  // Then deduplicate based on first name, last name, and DOB
  const uniquePatients = [];
  const patientKeys = new Set();
  for (const patient of sortedPatients) {
    const givenName = (patient.givenName || "").toLowerCase();
    const familyName = (patient.familyName || "").toLowerCase();
    const dob = patient.dateOfBirth || "";
    const key = `${givenName}|${familyName}|${dob}`;
    if (!patientKeys.has(key)) {
      patientKeys.add(key);
      uniquePatients.push(patient);
    }
  }
  return uniquePatients;
};
export function useLeadByDisplayId(displayId) {
  const $ = _c(17);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== displayId) {
    t0 = [LEADS_CACHE_KEY, displayId];
    $[0] = displayId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== displayId || $[3] !== queryClient) {
    t1 = async () => {
      const leadsCache = queryClient.getQueryData([LEADS_CACHE_KEY]);
      if (leadsCache) {
        const cachedLead = leadsCache.find(lead => lead.displayId === displayId);
        if (cachedLead) {
          return cachedLead;
        }
      }
      ;
      try {
        const lead_0 = await getPatientById(displayId);
        if (lead_0) {
          updateLeadsQueryData(queryClient, lead_0);
        }
        return lead_0;
      } catch (t2) {
        const err = t2;
        if (isNotFound(err as Error)) {
          return null;
        }
        throw err;
      }
    };
    $[2] = displayId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== displayId || $[6] !== queryClient) {
    t2 = (previousData, _query) => {
      const cachedLeadQuery = queryClient.getQueryData([LEADS_CACHE_KEY, displayId]);
      if (cachedLeadQuery) {
        return cachedLeadQuery;
      }
      const leadsCache_0 = queryClient.getQueryData([LEADS_CACHE_KEY]);
      if (leadsCache_0) {
        const cachedLead_0 = leadsCache_0.find(lead_1 => lead_1.displayId === displayId);
        if (cachedLead_0) {
          return cachedLead_0;
        }
      }
      return previousData;
    };
    $[5] = displayId;
    $[6] = queryClient;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      staleTime: Infinity,
      placeholderData: t2
    };
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    data: lead_2,
    isFetching,
    isLoading,
    isError
  } = useQuery(t3);
  let t4;
  if ($[12] !== isError || $[13] !== isFetching || $[14] !== isLoading || $[15] !== lead_2) {
    t4 = {
      lead: lead_2,
      isFetching,
      isLoading,
      isError
    };
    $[12] = isError;
    $[13] = isFetching;
    $[14] = isLoading;
    $[15] = lead_2;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
export function usePatientById(id) {
  const $ = _c(20);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== id || $[1] !== queryClient) {
    const patientsCache = queryClient.getQueryData([PATIENTS_CACHE_KEY]);
    bb0: {
      if (!patientsCache || !id) {
        t0 = undefined;
        break bb0;
      }
      let t1;
      if ($[3] !== id) {
        t1 = patient => patient.id === id;
        $[3] = id;
        $[4] = t1;
      } else {
        t1 = $[4];
      }
      t0 = patientsCache.find(t1);
    }
    $[0] = id;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const cachedPatient = t0;
  let t1;
  if ($[5] !== id) {
    t1 = [PATIENTS_CACHE_KEY, "byId", id];
    $[5] = id;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== id || $[8] !== queryClient) {
    t2 = async () => {
      ;
      try {
        const patient_0 = await getPatientById(id);
        if (patient_0) {
          updatePatientsQueryData(queryClient, patient_0);
        }
        return patient_0;
      } catch (t3) {
        const err = t3;
        if (isNotFound(err as Error)) {
          return null;
        }
        throw err;
      }
    };
    $[7] = id;
    $[8] = queryClient;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const t3 = !!id && !cachedPatient;
  let t4;
  if ($[10] !== cachedPatient || $[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: Infinity,
      placeholderData: cachedPatient
    };
    $[10] = cachedPatient;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const {
    data: fetchedPatient,
    isFetching,
    isLoading,
    isError
  } = useQuery(t4);
  const patient_1 = cachedPatient ?? fetchedPatient ?? null;
  let t5;
  if ($[15] !== isError || $[16] !== isFetching || $[17] !== isLoading || $[18] !== patient_1) {
    t5 = {
      patient: patient_1,
      isFetching,
      isLoading,
      isError
    };
    $[15] = isError;
    $[16] = isFetching;
    $[17] = isLoading;
    $[18] = patient_1;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
export function usePatientByDisplayId(displayId) {
  const $ = _c(22);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== displayId || $[1] !== queryClient) {
    const patientsCache = queryClient.getQueryData([PATIENTS_CACHE_KEY]);
    bb0: {
      if (!patientsCache || !displayId) {
        t0 = undefined;
        break bb0;
      }
      let t1;
      if ($[3] !== displayId) {
        t1 = patient => patient.displayId === displayId;
        $[3] = displayId;
        $[4] = t1;
      } else {
        t1 = $[4];
      }
      t0 = patientsCache.find(t1);
    }
    $[0] = displayId;
    $[1] = queryClient;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const cachedPatient = t0;
  let t1;
  if ($[5] !== displayId) {
    t1 = [PATIENTS_CACHE_KEY, displayId];
    $[5] = displayId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== displayId || $[8] !== queryClient) {
    t2 = async () => {
      ;
      try {
        const patient_0 = await getPatientById(displayId);
        if (patient_0) {
          updatePatientsQueryData(queryClient, patient_0);
        }
        return patient_0;
      } catch (t3) {
        const err = t3;
        if (isNotFound(err as Error)) {
          return null;
        }
        throw err;
      }
    };
    $[7] = displayId;
    $[8] = queryClient;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const t3 = !!displayId && !cachedPatient;
  let t4;
  if ($[10] !== cachedPatient) {
    t4 = previousData => {
      if (cachedPatient) {
        return cachedPatient;
      }
      return previousData;
    };
    $[10] = cachedPatient;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== t1 || $[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: Infinity,
      placeholderData: t4
    };
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const {
    data: fetchedPatient,
    isFetching,
    isLoading,
    isError
  } = useQuery(t5);
  const patient_1 = cachedPatient ?? fetchedPatient ?? null;
  let t6;
  if ($[17] !== isError || $[18] !== isFetching || $[19] !== isLoading || $[20] !== patient_1) {
    t6 = {
      patient: patient_1,
      isFetching,
      isLoading,
      isError
    };
    $[17] = isError;
    $[18] = isFetching;
    $[19] = isLoading;
    $[20] = patient_1;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  return t6;
}
export function usePatientsByIds(ids) {
  const $ = _c(6);
  const queryClient = useQueryClient();
  const uniqueIds = [...ids].sort();
  const sortedIdsKey = uniqueIds.join(",");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      queryKey: [PATIENTS_CACHE_KEY],
      queryFn: _temp,
      enabled: false,
      staleTime: Infinity
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    data: patientsCache
  } = useQuery(t0);
  let t1;
  bb0: {
    if (!patientsCache) {
      t1 = new Map();
      break bb0;
    }
    const map = new Map();
    for (const patient of patientsCache) {
      if (patient.id) {
        map.set(patient.id, patient);
      }
    }
    t1 = map;
  }
  const patientsMap = t1;
  const missingIds = uniqueIds.filter(id => !patientsMap.has(id));
  const {
    isFetching,
    isLoading,
    isError
  } = useQuery({
    queryKey: [PATIENTS_CACHE_KEY, "byIds", sortedIdsKey],
    queryFn: async () => {
      if (missingIds.length === 0) {
        return [];
      }
      const fetched = await getPatientsByIds(new Set(missingIds));
      for (const patient_0 of fetched) {
        updatePatientsQueryData(queryClient, patient_0);
      }
      return fetched;
    },
    enabled: missingIds.length > 0,
    staleTime: Infinity
  });
  const patients = uniqueIds.map(id_0 => patientsMap.get(id_0)).filter(_temp2);
  let t2;
  if ($[1] !== isError || $[2] !== isFetching || $[3] !== isLoading || $[4] !== patients) {
    t2 = {
      patients,
      isFetching,
      isLoading,
      isError
    };
    $[1] = isError;
    $[2] = isFetching;
    $[3] = isLoading;
    $[4] = patients;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}

/**
 * Light-weight companion to `usePatientsByIds` that returns only the
 * `PatientMetadata` fields (name, displayId, phones, …) for the given ids.
 * Use this when the caller just needs to render a label / avatar / link and
 * doesn't need the full nested `Patient` model — e.g. the comms inbox
 * sidebar listing one row per conversation partner.
 */
function _temp2(p) {
  return p !== undefined;
}
function _temp() {
  return Promise.resolve([]);
}
export function usePatientsMetadataByIds(ids) {
  const $ = _c(18);
  let t0;
  let uniqueIds;
  if ($[0] !== ids) {
    uniqueIds = [...ids].sort();
    t0 = uniqueIds.join(",");
    $[0] = ids;
    $[1] = t0;
    $[2] = uniqueIds;
  } else {
    t0 = $[1];
    uniqueIds = $[2];
  }
  const sortedIdsKey = t0;
  let t1;
  if ($[3] !== sortedIdsKey) {
    t1 = [PATIENTS_METADATA_BY_IDS_CACHE_KEY, sortedIdsKey];
    $[3] = sortedIdsKey;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== uniqueIds) {
    t2 = () => getPatientsMetadataByIds(new Set(uniqueIds));
    $[5] = uniqueIds;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = uniqueIds.length > 0;
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: 60000,
      placeholderData: _temp3
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    data,
    isFetching,
    isLoading,
    isError
  } = useQuery(t4);
  let t5;
  if ($[11] !== data) {
    t5 = data ?? [];
    $[11] = data;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const patients = t5;
  let t6;
  if ($[13] !== isError || $[14] !== isFetching || $[15] !== isLoading || $[16] !== patients) {
    t6 = {
      patients,
      isFetching,
      isLoading,
      isError
    };
    $[13] = isError;
    $[14] = isFetching;
    $[15] = isLoading;
    $[16] = patients;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}

/**
 * Server-side patient search backed by the `patients_metadata` view's trigram
 * index. Returns full `Patient[]` for matched rows so callers can use the
 * results directly in the EntityMatcher (which copies fields off of the
 * selected patient into the active block).
 */
function _temp3(previousData) {
  return previousData;
}
export function usePatientSearch(query, t0) {
  const $ = _c(24);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    enabled: t2,
    limit: t3,
    type
  } = options;
  const enabled = t2 === undefined ? true : t2;
  const limit = t3 === undefined ? 20 : t3;
  let t4;
  if ($[2] !== query) {
    t4 = query.trim();
    $[2] = query;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const trimmed = t4;
  const t5 = type ?? null;
  let t6;
  if ($[4] !== limit || $[5] !== t5 || $[6] !== trimmed) {
    t6 = [PATIENTS_SEARCH_CACHE_KEY, trimmed, limit, t5];
    $[4] = limit;
    $[5] = t5;
    $[6] = trimmed;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== limit || $[9] !== trimmed || $[10] !== type) {
    t7 = () => searchPatientsByQuery(trimmed, {
      limit,
      type
    });
    $[8] = limit;
    $[9] = trimmed;
    $[10] = type;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = enabled && trimmed.length >= 2;
  let t9;
  if ($[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = {
      queryKey: t6,
      queryFn: t7,
      enabled: t8,
      staleTime: 30000,
      placeholderData: _temp4
    };
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    data,
    isFetching,
    isLoading,
    isError,
    error
  } = useQuery(t9);
  let t10;
  if ($[16] !== data) {
    t10 = data ?? [];
    $[16] = data;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== error || $[19] !== isError || $[20] !== isFetching || $[21] !== isLoading || $[22] !== t10) {
    t11 = {
      patients: t10,
      isFetching,
      isLoading,
      isError,
      error
    };
    $[18] = error;
    $[19] = isError;
    $[20] = isFetching;
    $[21] = isLoading;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}
function _temp4(previousData) {
  return previousData;
}
export const updatePatientsQueryData = (queryClient: QueryClient, updatedPatient: Patient) => {
  queryClient.setQueryData([PATIENTS_CACHE_KEY], (oldPatients: Patient[] = []) => {
    const exists = oldPatients.some(p => p.id === updatedPatient.id);
    if (!exists) {
      return [...oldPatients, updatedPatient];
    }
    return oldPatients.map(p => p.id === updatedPatient.id ? updatedPatient : p);
  });
  queryClient.setQueryData(["patient", updatedPatient.displayId], updatedPatient);
  queryClient.setQueryData(["patient-by-id", updatedPatient.id], updatedPatient);
};
export const updateLeadsQueryData = (queryClient: QueryClient, updatedLead: Patient) => {
  queryClient.setQueryData([LEADS_CACHE_KEY], (oldLeads: Patient[] = []) => {
    const exists = oldLeads.some(l => l.id === updatedLead.id);
    if (!exists) {
      return [...oldLeads, updatedLead];
    }
    return oldLeads.map(l => l.id === updatedLead.id ? updatedLead : l);
  });
  queryClient.setQueryData(["lead", updatedLead.displayId], updatedLead);
  queryClient.setQueryData(["lead-by-id", updatedLead.id], updatedLead);
};
export const mutatePatientOptions = (patientId: string | null | undefined, queryClient: QueryClient): MutationOptions<void, Error, {
  patientId: string;
  newStatus: PatientStatus;
}, {
  previousPatients: Patient[];
}> => {
  if (!patientId) {
    return {
      mutationFn: () => Promise.resolve()
    };
  }
  return {
    mutationFn: async ({
      patientId,
      newStatus
    }: {
      patientId: string;
      newStatus: PatientStatus;
    }) => {
      return await updatePatientStatus(patientId, newStatus);
    },
    onMutate: async ({
      patientId,
      newStatus
    }) => {
      await queryClient.cancelQueries({
        queryKey: [PATIENTS_CACHE_KEY]
      });
      const previousPatients = queryClient.getQueryData<Patient[]>([PATIENTS_CACHE_KEY]) ?? [];
      queryClient.setQueryData([PATIENTS_CACHE_KEY], (patients: Patient[] = []) => patients.map(p => p.id === patientId ? {
        ...p,
        status: newStatus
      } : p));
      return {
        previousPatients
      };
    },
    onError: (error, variables, context) => {
      logger.error("Error updating patient");
      if (context) {
        queryClient.setQueryData([PATIENTS_CACHE_KEY], context.previousPatients);
      }
      toast({
        title: "Something went wrong updating the patient. Please refresh the page and try again.",
        description: error.message,
        variant: "destructive"
      });
    }
  };
};
export function useCreateOrUpdatePatientMutation(t0) {
  const $ = _c(12);
  const overwriteWithNull = t0 === undefined ? true : t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== overwriteWithNull) {
    t1 = async patient => createOrUpdatePatient(patient, overwriteWithNull);
    $[0] = overwriteWithNull;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  let t4;
  if ($[2] !== queryClient) {
    t2 = async patient_0 => {
      await queryClient.cancelQueries({
        queryKey: [PATIENTS_CACHE_KEY]
      });
      const isLead = patient_0.type === PatientType.LEAD;
      if (isLead) {
        await queryClient.cancelQueries({
          queryKey: [LEADS_CACHE_KEY]
        });
      }
      const previousPatients = queryClient.getQueryData([PATIENTS_CACHE_KEY]) ?? [];
      const previousLeads = isLead ? queryClient.getQueryData([LEADS_CACHE_KEY]) ?? [] : [];
      queryClient.setQueryData([PATIENTS_CACHE_KEY], t5 => {
        const oldPatients = t5 === undefined ? [] : t5;
        const patientExists = oldPatients.some(p => p.id === patient_0.id);
        if (patientExists) {
          return oldPatients.map(p_0 => p_0.id === patient_0.id ? patient_0 : p_0);
        } else {
          return [patient_0, ...oldPatients];
        }
      });
      if (isLead) {
        queryClient.setQueryData([LEADS_CACHE_KEY], t6 => {
          const oldLeads = t6 === undefined ? [] : t6;
          const leadExists = oldLeads.some(l => l.id === patient_0.id);
          if (leadExists) {
            return oldLeads.map(l_0 => l_0.id === patient_0.id ? patient_0 : l_0);
          } else {
            return [patient_0, ...oldLeads];
          }
        });
      }
      if (patient_0.displayId) {
        queryClient.setQueryData([PATIENTS_CACHE_KEY, patient_0.displayId], patient_0);
        if (isLead) {
          queryClient.setQueryData([LEADS_CACHE_KEY, patient_0.displayId], patient_0);
        }
      }
      return {
        previousPatients,
        previousLeads,
        isLead
      };
    };
    t3 = updatedPatient => {
      queryClient.setQueryData([PATIENTS_CACHE_KEY], t7 => {
        const oldPatients_0 = t7 === undefined ? [] : t7;
        const patientExists_0 = oldPatients_0.some(p_1 => p_1.id === updatedPatient.id);
        if (patientExists_0) {
          return oldPatients_0.map(p_2 => p_2.id === updatedPatient.id ? updatedPatient : p_2);
        } else {
          return [updatedPatient, ...oldPatients_0];
        }
      });
      if (updatedPatient.type === PatientType.LEAD) {
        updateLeadsQueryData(queryClient, updatedPatient);
      }
    };
    t4 = (error, patient_1, context) => {
      logger.error("Error creating/updating patient:");
      if (context) {
        queryClient.setQueryData([PATIENTS_CACHE_KEY], context.previousPatients);
        if (patient_1.displayId) {
          const previousPatient = context.previousPatients.find(p_3 => p_3.displayId === patient_1.displayId);
          if (previousPatient) {
            queryClient.setQueryData([PATIENTS_CACHE_KEY, patient_1.displayId], previousPatient);
          }
        }
        if (context.isLead && context.previousLeads) {
          queryClient.setQueryData([LEADS_CACHE_KEY], context.previousLeads);
          if (patient_1.displayId) {
            const previousLead = context.previousLeads.find(l_1 => l_1.displayId === patient_1.displayId);
            if (previousLead) {
              queryClient.setQueryData([LEADS_CACHE_KEY, patient_1.displayId], previousLead);
            }
          }
        }
      }
      toast({
        title: "Failed to save patient",
        description: "There was an error saving the patient. Please try again.",
        variant: "destructive"
      });
    };
    $[2] = queryClient;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      invalidates: [[TASKS_CACHE_KEY], [TASK_CACHE_KEY], [REFERRALS_CACHE_KEY]]
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t6 = {
      mutationFn: t1,
      onMutate: t2,
      onSuccess: t3,
      onError: t4,
      meta: t5
    };
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return useMutation(t6);
}

/**
 * Custom hook for paginated patient metadata fetching using the patients_metadata view
 */
export function usePaginatedPatientsMetadata(t0) {
  const $ = _c(15);
  const {
    columnFilters,
    page: t1,
    pageSize: t2,
    sortBy,
    sortOrder,
    query,
    enabled: t3
  } = t0 === undefined ? {} : t0;
  const page = t1 === undefined ? 0 : t1;
  const pageSize = t2 === undefined ? 30 : t2;
  const enabled = t3 === undefined ? true : t3;
  const queryClient = useQueryClient();
  const typeFilter = columnFilters && "type" in columnFilters ? columnFilters.type : [PatientType.ESTABLISHED];
  const patientsQuery = useQuery({
    queryKey: [PAGINATED_PATIENTS_METADATA_CACHE_KEY, {
      ...columnFilters,
      type: typeFilter
    }, page, pageSize, sortBy, sortOrder, query],
    queryFn: () => getPaginatedPatientsMetadata({
      page,
      pageSize,
      status: columnFilters?.status,
      type: typeFilter,
      sortBy,
      sortOrder,
      query
    }),
    enabled,
    staleTime: 5000,
    refetchInterval: enabled ? 5000 : false,
    placeholderData: _temp5
  });
  const paginatedData = patientsQuery.data;
  let t4;
  if ($[0] !== paginatedData?.patients) {
    t4 = paginatedData?.patients || {};
    $[0] = paginatedData?.patients;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const patientsMap = t4;
  const hasMore = (!patientsQuery?.isFetching && paginatedData?.hasMore) ?? false;
  if (hasMore) {
    queryClient.prefetchQuery({
      queryKey: [PAGINATED_PATIENTS_METADATA_CACHE_KEY, columnFilters, page + 1, pageSize, sortBy, sortOrder, query],
      queryFn: () => getPaginatedPatientsMetadata({
        page: page + 1,
        pageSize,
        status: columnFilters?.status,
        type: columnFilters?.type,
        sortBy,
        sortOrder,
        query
      }),
      staleTime: 5000
    });
  }
  let t5;
  if ($[2] !== patientsMap) {
    t5 = Object.values(patientsMap);
    $[2] = patientsMap;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = paginatedData?.totalCount || 0;
  const t7 = paginatedData?.hasMore || false;
  const t8 = paginatedData?.page || page;
  const t9 = paginatedData?.pageSize || pageSize;
  let t10;
  if ($[4] !== patientsMap || $[5] !== patientsQuery.isError || $[6] !== patientsQuery.isFetching || $[7] !== patientsQuery.isLoading || $[8] !== patientsQuery.refetch || $[9] !== t5 || $[10] !== t6 || $[11] !== t7 || $[12] !== t8 || $[13] !== t9) {
    t10 = {
      patientsMap,
      patients: t5,
      totalCount: t6,
      hasMore: t7,
      page: t8,
      pageSize: t9,
      isFetching: patientsQuery.isFetching,
      isLoading: patientsQuery.isLoading,
      isError: patientsQuery.isError,
      refetch: patientsQuery.refetch
    };
    $[4] = patientsMap;
    $[5] = patientsQuery.isError;
    $[6] = patientsQuery.isFetching;
    $[7] = patientsQuery.isLoading;
    $[8] = patientsQuery.refetch;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
}
function _temp5(previousData) {
  return previousData;
}